import type { FC } from "react";

import {
  Navigate,
  NoContextOutlet,
  type RouteObject,
  useMatch,
} from "src/router";
import { DetailPage } from "src/components/layout";
import { useHasProductTabs } from "src/components/layout/nav/sections/hooks/projects";
import { useWorkspaceSettingsTabs } from "src/pages/settings/tabs";
import { lazyNamed } from "src/utils/lazy-named";
import type { RoutePreload } from "src/router/route-preloads";
const ManageSupportAccess = lazyNamed(
  () => import("src/components/support-access"),
  "ManageSupportAccess",
);
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { EntitledRoute } from "src/components/router/entitled-route";

const APIKeys = lazyNamed(() => import("./api-keys"), "APIKeys");
const AuditLog = lazyNamed(() => import("./audit-log"), "AuditLog");
const EntitlementCenter = lazyNamed(
  () => import("./entitlement-center"),
  "EntitlementCenter",
);
const CloudProviders = lazyNamed(
  () => import("./cloud-providers"),
  "CloudProviders",
);
const Members = lazyNamed(() => import("./members"), "Members");
const Storage = lazyNamed(() => import("./storage"), "Storage");
const Tunnels = lazyNamed(() => import("./tunnels"), "Tunnels");
const workspaceImport = () => import("./workspace");
const Workspace = lazyNamed(workspaceImport, "Workspace");

export const preloads: RoutePreload[] = [["/settings", workspaceImport]];
const Groups = lazyNamed(() => import("./groups"), "Groups");
const LabelsSettings = lazyNamed(() => import("./labels"), "LabelsSettings");
const Spaces = lazyNamed(() => import("./spaces"), "Spaces");
const RoleWorkspaceGrants = lazyNamed(
  () => import("src/pages/organizations/roles/role/workspaces/workspace"),
  "RoleWorkspacePolicy",
);
const DestinationGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/destinations"
    ),
  "DestinationGrants",
);
const SourceGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/sources"),
  "SourceGrants",
);
const ParentModelGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/parent-models"
    ),
  "ParentModelGrants",
);
const GeneralGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/general"),
  "GeneralGrants",
);
const AgentsGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/agents"),
  "AgentsGrants",
);
const CepConsentGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/cep-consent"
    ),
  "CepConsentGrants",
);
const NativeDeliveryGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/native-delivery"
    ),
  "NativeDeliveryGrants",
);
const DecisionEngineGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/decision-engines"
    ),
  "DecisionEngineGrants",
);

const SpaceGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/spaces"),
  "SpaceGrants",
);
const EventsGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/events"),
  "EventsGrants",
);

// SSO is managed at the organization level.
const SSORoute: FC = () => {
  return <Navigate to="/organization/sso" replace />;
};

// Billing is managed at the organization level.
const BillingRoute: FC = () => {
  return <Navigate to="/organization/billing" replace />;
};

const Layout: FC = () => {
  const tabs = useWorkspaceSettingsTabs();
  const hasProductTabs = useHasProductTabs();
  const activePath = useMatch("/settings/:tab/*")?.params.tab;
  const activeTab = tabs.find((tab) => tab.path === activePath);

  // With the product tabs, the settings nav lists these pages, so the page drops its tabs.
  if (hasProductTabs && activeTab) {
    return (
      <DetailPage
        heading={activeTab.title}
        variant={activeTab.variant}
        hasBottomPadding={activeTab.hasBottomPadding ?? true}
      >
        <NoContextOutlet />
      </DetailPage>
    );
  }

  return (
    <DetailPage
      tabs={tabs}
      tabDepth={3}
      heading="Workspace settings"
      hasBottomPadding
    >
      <NoContextOutlet />
    </DetailPage>
  );
};

export const routes: Array<RouteObject> = [
  {
    path: "settings/*",
    element: <Layout />,
    spacesAccess: "hidden-from-limited-view",
    children: [
      {
        path: "workspace",
        element: <Workspace />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "members",
        element: <Members />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "labels",
        element: (
          <FeatureFlagRoute flagKey="cepLabels" redirect="/settings/members">
            <LabelsSettings />
          </FeatureFlagRoute>
        ),
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "groups",
        element: <Groups />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            path: ":roleId/workspaces/:workspaceId",
            element: <RoleWorkspaceGrants redirectToWorkspaceSettings />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                index: true,
                element: (
                  <Navigate
                    to={{ pathname: "general", search: location.search }}
                    replace
                  />
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "general",
                element: <GeneralGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "spaces",
                element: (
                  <PermissionedRoute
                    permission={{
                      v2: {
                        resource: "workspace",
                        grant: "is_workspace_admin",
                      },
                    }}
                    redirect="general"
                  >
                    <SpaceGrants />
                  </PermissionedRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "sources",
                element: <SourceGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "destinations",
                element: <DestinationGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "parent-models",
                element: <ParentModelGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "agents",
                element: <AgentsGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "ai-decisioning",
                element: (
                  <EntitledRoute
                    entitlement="decisionEngine"
                    redirect="general"
                  >
                    <DecisionEngineGrants />
                  </EntitledRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "cep-consent",
                element: (
                  <EntitledRoute entitlement="cep" redirect="general">
                    <CepConsentGrants />
                  </EntitledRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "native-delivery",
                element: (
                  <EntitledRoute entitlement="cep" redirect="general">
                    <NativeDeliveryGrants />
                  </EntitledRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "events/*",
                element: (
                  <FeatureFlagRoute
                    flagKey="eventsRbacEnabled"
                    redirect="general"
                  >
                    <EventsGrants />
                  </FeatureFlagRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
        ],
      },
      {
        path: "spaces/*",
        element: (
          <PermissionedRoute
            permission={{
              v2: { resource: "workspace", grant: "is_workspace_admin" },
            }}
            redirect="/settings/members"
          >
            <Spaces />
          </PermissionedRoute>
        ),
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "billing",
        element: <BillingRoute />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "cloud-providers",
        element: <CloudProviders />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "api-keys",
        element: <APIKeys />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "storage",
        element: <Storage />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "tunnels/*",
        element: <Tunnels />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "sso",
        element: <SSORoute />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "audit-log",
        element: <AuditLog />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "entitlement-center",
        element: <EntitlementCenter />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "support-access",
        element: <ManageSupportAccess level="workspace" />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        index: true,
        element: (
          <Navigate
            to={{ pathname: "members", search: location.search }}
            replace
          />
        ),
        spacesAccess: "hidden-from-limited-view",
      },
    ],
  },
];
